import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  CssBaseline,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useThemeMode } from './context/ThemeContext';
import LoginForm from './components/LoginForm';
import SaleOrderForm from './components/SaleOrderFormSimple';
import SaleOrderList from './components/SaleOrderList';
import CustomerForm from './components/CustomerForm';
import Dashboard from './components/Dashboard';
import HelpPage from './components/HelpPage';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const Navigation: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!isAuthenticated) return null;

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChangePasswordOpen = () => {
    handleProfileMenuClose();
    setChangePasswordOpen(true);
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleChangePasswordClose = () => {
    setChangePasswordOpen(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/auth/change-password`,
        {
          old_password: oldPassword,
          new_password: newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPasswordSuccess('Contraseña cambiada exitosamente. Cerrando sesión...');

      // Cerrar sesión después de 2 segundos
      setTimeout(() => {
        handleChangePasswordClose();
        logout();
      }, 2000);
    } catch (error: any) {
      setPasswordError(
        error.response?.data?.detail || 'Error al cambiar la contraseña'
      );
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuItemClick = () => {
    setMobileOpen(false);
  };

  const menuItems = [
    { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { text: 'Crear Pedido', path: '/create-order', icon: <AddShoppingCartIcon /> },
    { text: 'Ver Pedidos', path: '/orders', icon: <ListAltIcon /> },
    { text: 'Crear Cliente', path: '/create-customer', icon: <PersonAddIcon /> },
    { text: 'Ayuda', path: '/help', icon: <HelpOutlineIcon /> },
  ];

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6" noWrap>
          Sistema de Ventas
        </Typography>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={handleMenuItemClick}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => { handleChangePasswordOpen(); handleMenuItemClick(); }}>
            <ListItemIcon>
              <LockIcon />
            </ListItemIcon>
            <ListItemText primary="Cambiar Contraseña" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={toggleTheme}>
            <ListItemIcon>
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </ListItemIcon>
            <ListItemText primary={mode === 'dark' ? 'Modo Claro' : 'Modo Oscuro'} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              logout();
              handleMenuItemClick();
            }}
          >
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Salir" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            Sistema de Ventas - Odoo
          </Typography>
          {!isMobile && (
            <>
              <Button
                color="inherit"
                component={Link}
                to="/dashboard"
                startIcon={<DashboardIcon />}
              >
                Dashboard
              </Button>
              <Button
                color="inherit"
                component={Link}
                to="/create-order"
                startIcon={<AddShoppingCartIcon />}
              >
                Crear Pedido
              </Button>
              <Button
                color="inherit"
                component={Link}
                to="/orders"
                startIcon={<ListAltIcon />}
              >
                Ver Pedidos
              </Button>
              <Button
                color="inherit"
                component={Link}
                to="/create-customer"
                startIcon={<PersonAddIcon />}
              >
                Crear Cliente
              </Button>
              <Button
                color="inherit"
                component={Link}
                to="/help"
                startIcon={<HelpOutlineIcon />}
              >
                Ayuda
              </Button>
              <Button
                color="inherit"
                onClick={handleProfileMenuOpen}
                startIcon={<AccountCircleIcon />}
              >
                Mi Perfil
              </Button>
            </>
          )}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleChangePasswordOpen}>
              <ListItemIcon>
                <LockIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cambiar Contraseña</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { toggleTheme(); handleProfileMenuClose(); }}>
              <ListItemIcon>
                {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{mode === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { logout(); handleProfileMenuClose(); }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Salir</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Diálogo de cambio de contraseña */}
      <Dialog open={changePasswordOpen} onClose={handleChangePasswordClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Cambiar Contraseña
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}
          <TextField
            margin="normal"
            fullWidth
            label="Contraseña Actual"
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Nueva Contraseña"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Confirmar Nueva Contraseña"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleChangePasswordClose}>Cancelar</Button>
          <Button onClick={handleChangePassword} variant="contained">
            Cambiar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { mode, toggleTheme } = useThemeMode();

  return (
    <>
      <CssBaseline />
      {!isAuthenticated && (
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <IconButton
            color="inherit"
            onClick={toggleTheme}
            aria-label="toggle theme"
          >
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Box>
      )}
      <Navigation />
      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 3 } }}>
        <Box sx={{ mt: { xs: 0, sm: 2 } }}>
          <Routes>
            <Route
              path="/login"
              element={!isAuthenticated ? <LoginForm /> : <Navigate to="/dashboard" />}
            />
            <Route
              path="/create-order"
              element={
                <PrivateRoute>
                  <SaleOrderForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/edit-order/:orderId"
              element={
                <PrivateRoute>
                  <SaleOrderForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <PrivateRoute>
                  <SaleOrderList />
                </PrivateRoute>
              }
            />
            <Route
              path="/create-customer"
              element={
                <PrivateRoute>
                  <CustomerForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/help"
              element={
                <PrivateRoute>
                  <HelpPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/"
              element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} />}
            />
          </Routes>
        </Box>
      </Container>
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;